import React from 'react';
import { Body, Link } from '@grnhse/seedling/lib/birch/typography';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { SecondaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import { withLocalePrefix } from 'shared/utils/translation';
import SingleApplication from './application_history';
import type { ApplicationHistoryItemProps } from 'application_review/components/shared/types';
import { useApplicationHistory } from 'talent_matching/api';
import SkeletonLoader from '@grnhse/seedling/lib/birch/components/skeleton_loader';
import { Document } from 'talent_matching/types';

const t = withLocalePrefix('talent_matching.panel.application_history');

type Props = {
  historyUrl: string | undefined;
  activeDocument?: Document | null;
  onOpenDocument?: (document: Document) => void;
  canDownloadCandidateData?: boolean;
  isRediscovery?: boolean;
};

type ApplicationHistory = {
  allApplicationsCount: number;
  previousApplications: ApplicationHistoryItemProps[];
  activeApplications: ApplicationHistoryItemProps[];
  candidateProfileUrl: string;
  showSeeAllLink: boolean;
  attachments: Document[];
};

// NOTE: This component is very similar to application_review/components/application_history/application_history.tsx
// Any changes should be made in both places.
const ApplicationHistoryTabPanel = ({
  historyUrl,
  activeDocument,
  onOpenDocument,
  canDownloadCandidateData = false,
  isRediscovery = false,
}: Props) => {
  const { data, isLoading, isFetching, error, refetch } = useApplicationHistory(
    historyUrl as string
  );

  if (isLoading || isFetching) {
    return <SkeletonLoader columnsPerRow={1} rows={5} />;
  }

  if (error) {
    return (
      <Stack>
        <Body>{t('error')}</Body>
        <SecondaryButton onClick={() => refetch()} size="md">
          {t('retry')}
        </SecondaryButton>
      </Stack>
    );
  }

  const applicationHistory: ApplicationHistory = data;

  return (
    <Stack spacing={2}>
      {data && (
        <Stack spacing={2}>
          {applicationHistory.activeApplications.length > 0 && (
            <Stack spacing={2}>
              <Body medium>{t('active_applications')}</Body>
              {applicationHistory.activeApplications.map((app) => (
                <SingleApplication
                  key={app.id}
                  application={app}
                  activeDocument={activeDocument}
                  onOpenDocument={onOpenDocument}
                  canDownloadCandidateData={canDownloadCandidateData}
                  isRediscovery={isRediscovery}
                />
              ))}
            </Stack>
          )}

          {applicationHistory.previousApplications.length > 0 && (
            <Stack spacing={2}>
              <Body medium>{t('previous_applications')}</Body>
              {applicationHistory.previousApplications.map((app) => (
                <SingleApplication
                  key={app.id}
                  application={app}
                  activeDocument={activeDocument}
                  onOpenDocument={onOpenDocument}
                  canDownloadCandidateData={canDownloadCandidateData}
                  isRediscovery={isRediscovery}
                />
              ))}
            </Stack>
          )}

          {applicationHistory.showSeeAllLink && (
            <Link href={applicationHistory.candidateProfileUrl} target="_blank">
              {t('see_all', {
                applications_count: applicationHistory.allApplicationsCount,
              })}
            </Link>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default ApplicationHistoryTabPanel;
